/**
 * Componente para ejecutar código en sandbox
 */
import { useState } from 'react';
import { API_URL, API } from '../config';
import { Play, Loader2, AlertCircle, CheckCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';


const CodeRunner = ({ code, language: codeLang, onClose }) => {
  const { language } = useLanguage();
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [execTime, setExecTime] = useState(null);
  const [success, setSuccess] = useState(null);

  const txt = {
    es: {
      runCode: 'Ejecutar',
      running: 'Ejecutando...',
      output: 'Salida',
      error: 'Error',
      executionTime: 'Tiempo de ejecución',
      seconds: 'segundos',
      unsupportedLang: 'Lenguaje no soportado',
      supportedLangs: 'Lenguajes soportados: Python, JavaScript',
      close: 'Cerrar',
      noOutput: 'Sin salida',
      runningNote: 'Ejecutando en sandbox seguro...',
    },
    en: {
      runCode: 'Run',
      running: 'Running...',
      output: 'Output',
      error: 'Error',
      executionTime: 'Execution time',
      seconds: 'seconds',
      unsupportedLang: 'Unsupported language',
      supportedLangs: 'Supported languages: Python, JavaScript',
      close: 'Close',
      noOutput: 'No output',
      runningNote: 'Running in secure sandbox...',
    }
  };
  const t = txt[language] || txt.es;

  // Check if language is supported
  const supportedLangs = ['python', 'python3', 'py', 'javascript', 'js', 'node', 'nodejs'];
  const isSupported = supportedLangs.includes(codeLang?.toLowerCase());

  const handleRun = async () => {
    if (!isSupported) {
      toast.error(t.supportedLangs);
      return;
    }

    setIsRunning(true);
    setOutput('');
    setError('');
    setSuccess(null);
    setExecTime(null);

    try {
      const response = await axios.post(`${API}/execute-code`, {
        code: code,
        language: codeLang,
        timeout: 10
      });

      const result = response.data;
      setSuccess(result.success);
      setExecTime(result.execution_time);

      if (result.success) {
        setOutput(result.output || t.noOutput);
      } else {
        setError(result.error || 'Unknown error');
      }
    } catch (err) {
      setSuccess(false);
      setError(err.response?.data?.detail || err.message || 'Error executing code');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="mt-2 border border-zinc-700 rounded-lg overflow-hidden bg-zinc-900/50">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-zinc-800/50 border-b border-zinc-700">
        <div className="flex items-center gap-2">
          <button
            onClick={handleRun}
            disabled={isRunning || !isSupported}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              isSupported
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-zinc-600 text-zinc-400 cursor-not-allowed'
            }`}
          >
            {isRunning ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t.running}
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                {t.runCode}
              </>
            )}
          </button>
          {!isSupported && (
            <span className="text-xs text-zinc-500">{t.supportedLangs}</span>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 hover:bg-zinc-700 rounded transition-colors"
            title={t.close}
          >
            <X className="w-4 h-4 text-zinc-400" />
          </button>
        )}
      </div>

      {/* Output/Error Area */}
      {(output || error || isRunning) && (
        <div className="p-3">
          {isRunning && (
            <div className="flex items-center gap-2 text-zinc-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t.runningNote}
            </div>
          )}

          {!isRunning && success !== null && (
            <>
              {/* Status indicator */}
              <div className={`flex items-center gap-2 mb-2 text-sm ${success ? 'text-green-400' : 'text-red-400'}`}>
                {success ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <AlertCircle className="w-4 h-4" />
                )}
                <span>{success ? t.output : t.error}</span>
                {execTime !== null && (
                  <span className="text-zinc-500 text-xs ml-auto">
                    {t.executionTime}: {execTime}s
                  </span>
                )}
              </div>

              {/* Output content */}
              <pre className={`p-3 rounded text-sm font-mono overflow-x-auto max-h-48 ${
                success 
                  ? 'bg-zinc-800/50 text-zinc-200' 
                  : 'bg-red-900/20 text-red-300'
              }`}>
                {success ? output : error}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CodeRunner;
